import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import type { StaffMyPagesConfig } from "../types";
import { UserDetail } from "./components/UserDetail";
import { IncomeComp } from "./components/IncomeComp";
import { CardComp } from "./components/CardComp";
import BottomNav from "@/components/feature/BottomNav";
import BecomeExpertDrawer from "./components/BecomeExpertDrawer";
import { getMyConfig } from "@/api/staff";
import { resolveUrl } from "@/utils/workspace-url";
import type { BalanceItem } from "../../types";

const BRAND_GRADIENT = "linear-gradient(135deg, #FF6B35, #FF9A5C)";

export default function WorkspaceProfilePage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<StaffMyPagesConfig | null>(null);
  const [showBecomeExpert, setShowBecomeExpert] = useState(false);

  // 邀请码菜单项（插入到"我的项目"前面）
  const inviteCodeItem: BalanceItem = {
    id: -999,
    name: "邀请码",
    pic: "",
    url: "",
  };

  // 处理其他菜单列表，在"我的项目"前面插入"邀请码"
  const getRestMenuWithInvite = (): BalanceItem[] => {
    const list = config?.config?.staff_rest_menu || [];
    const myProjectIndex = list.findIndex((item) => item.name === "我的项目");
    if (myProjectIndex > 0) {
      const newList = [...list];
      newList.splice(myProjectIndex, 0, inviteCodeItem);
      return newList;
    }
    // 如果找不到"我的项目"，插入到最前面
    return [inviteCodeItem, ...list];
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getMyConfig();
        if (alive) setConfig(res as unknown as StaffMyPagesConfig);
      } catch {
        // 错误提示在 request 层统一处理
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!config) {
    return (
      <div
        className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+80px)]"
        style={{
          background: "#F7F7F7",
          fontFamily:
            "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif",
        }}
      >
        {/* Header 骨架 */}
        <div className="bg-white border-b border-gray-100">
          <div className="flex items-center px-4 py-3 h-[52px]" />
        </div>
        {/* 内容骨架 */}
        <div className="px-4 pt-4 space-y-3 animate-pulse">
          <div className="h-[88px] rounded-2xl bg-gray-200" />
          <div className="h-[72px] rounded-2xl bg-gray-200" />
          <div className="h-[160px] rounded-2xl bg-gray-200" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+80px)] relative"
      style={{
        background: "#F7F7F7",
        fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif",
      }}
    >
      {/* 顶部背景（对齐 uni-app 的 staff_user_bg） */}
      {!!config.config?.staff_user_bg && (
        <img
          src={config.config.staff_user_bg}
          alt=""
          className="absolute left-0 top-0 w-full object-cover"
          style={{ height: 260, zIndex: 0, background: "#fff" }}
        />
      )}

      {/* Header */}
      <div
        className="px-5 flex items-center sticky top-0 z-10"
        style={{
          paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)",
          paddingBottom: 13,
          borderBottom: "1px solid #F5F5F5",
          background: "transparent",
          backdropFilter: "saturate(180%) blur(8px)",
        }}
      >
        <span className="text-lg font-bold text-gray-800">我的</span>
      </div>

      <div className="px-4 pt-4 space-y-3 relative z-[1]">
        {/* 用户信息卡片 — 带品牌渐变头部 */}
        <div className="bg-white rounded-2xl overflow-hidden">
          <div className="p-5" style={{ background: BRAND_GRADIENT }}>
            <UserDetail user={config.staff} isDark />
          </div>
        </div>

        {/* 收入展示 */}
        <div className="bg-white rounded-2xl p-5">
          <IncomeComp income={config.income} />
        </div>

        {/* 结算与统计 */}
        {(config.config.balance_and_statistics?.length || 0) > 0 && (
          <CardComp
            title="结算与统计"
            list={config.config.balance_and_statistics}
          />
        )}

        {/* 其他菜单 */}
        {(config.config.staff_rest_menu?.length || 0) > 0 && (
          <div className="bg-white rounded-2xl overflow-hidden">
            {/* 标题栏 */}
            <div className="flex items-center px-4 pt-4 pb-3">
              <span className="w-[3px] h-[18px] rounded-full mr-2.5 bg-[#FF6B35]" />
              <span className="text-base font-bold text-gray-900">其他</span>
            </div>
            {/* 菜单列表 */}
            <div>
              {getRestMenuWithInvite().map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.name === "邀请码") {
                      setShowBecomeExpert(true);
                    } else if (item.url) {
                      navigate(resolveUrl(item.url));
                    }
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-gray-50 transition-colors text-left border-t border-gray-50"
                >
                  {/* 图标容器 */}
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: "#FFF3EE" }}
                  >
                    {item.name === "邀请码" ? (
                      <i className="ri-qr-scan-line text-lg" style={{ color: "#FF6B35" }} />
                    ) : item.pic ? (
                      <img src={item.pic} alt="" className="w-5 h-5 object-contain" />
                    ) : (
                      <i className="ri-arrow-right-s-line text-base" style={{ color: "#FF6B35" }} />
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-800 flex-1">{item.name}</span>
                  <i className="ri-arrow-right-s-line text-gray-300" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 成为达人抽屉 */}
      <BecomeExpertDrawer
        visible={showBecomeExpert}
        onClose={() => setShowBecomeExpert(false)}
      />

      {/* 底部导航栏 */}
      <BottomNav />
    </div>
  );
}
